import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  obtenerMensajes,
  enviarMensaje,
  obtenerComercio,
  obtenerIndiceRespuesta,
  registrarTap
} from "../api";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { useDynamicSplash } from "../hooks/useDynamicSplash";

const Chat = () => {
  const { id_comercio, id_sucursal } = useParams();
  const { user, authReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [comercio, setComercio] = useState(null);
  const [indiceRespuesta, setIndiceRespuesta] = useState(null);
  const [loadingChat, setLoadingChat] = useState(true);
  const showSplash = useDynamicSplash(loadingChat);
  const [headerVisible, setHeaderVisible] = useState(true);

  const chatRef = useRef(null);
  const headerRef = useRef(null);
  const lastScroll = useRef(0);
  const origen = location.state?.from || "comercio";

  const handleBack = () => {
    if (origen === "chats") {
      navigate("/chats");
    } else {
      navigate(`/comercio/app/${id_sucursal}`);
    }
  };

  const irADetalleComercio = () => {
    registrarTap(id_sucursal, "perfil_chat", user.id);
    setTimeout(() => {
      navigate(`/comercio/${id_comercio}/${id_sucursal}`);
    }, 200);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (!authReady || !user?.id) {
      navigate("/login");
      return;
    }

    if (origen === "chats") {
      window.history.replaceState({}, "", "/chats");
    } else {
      window.history.replaceState({}, "", `/comercio/app/${id_sucursal}`);
    }
    window.history.pushState({}, "", window.location.pathname);

    const cargarTodo = async () => {
      try {
        setLoadingChat(true);
        const [mensajesData, comercioData, indiceData] = await Promise.all([
          obtenerMensajes(id_comercio, id_sucursal, user.id),
          obtenerComercio(id_comercio),
          obtenerIndiceRespuesta(id_comercio)
        ]);

        setMensajes(Array.isArray(mensajesData) ? mensajesData : []);
        setComercio(comercioData || null);
        setIndiceRespuesta(indiceData || null);
      } catch (err) {
      } finally {
        setLoadingChat(false);
        document.body.style.overflow = "auto";
        window.scrollTo(0, 0);
      }
    };

    cargarTodo();
  }, [authReady, user, id_comercio, id_sucursal]);

  useEffect(() => {
    const handleScroll = () => {
      if (!chatRef.current || !headerRef.current) return;
      const currentScroll = chatRef.current.scrollTop;
      if (currentScroll > lastScroll.current && currentScroll > 50) {
        headerRef.current.style.transform = "translateY(-100%)";
        headerRef.current.style.transition = "transform 0.3s ease";
      } else {
        headerRef.current.style.transform = "translateY(0)";
        headerRef.current.style.transition = "transform 0.3s ease";
      }
      lastScroll.current = currentScroll;
    };

    const scrollContainer = chatRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll);
    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, []);

  const handleEnviar = async () => {
    if (nuevoMensaje.trim() === "") return;

    const mensaje = {
      id_comercio,
      id_sucursal,
      id_perfil: user.id,
      texto: nuevoMensaje
    };

    const enviado = await enviarMensaje(mensaje);
    if (enviado.success) {
      const nuevo = {
        ...enviado.mensaje,
        timestamp: enviado.mensaje.timestamp
      };
      setMensajes((prev) => [...prev, nuevo]);
      setNuevoMensaje("");
    }
  };

  useEffect(() => {
    chatRef.current?.scrollTo(0, chatRef.current.scrollHeight);
  }, [mensajes]);

  if (!comercio) {
    return (
      <div className="chat-container text-center p-5">
        <p></p>
      </div>
    );
  }

  return (
    <>
      {showSplash && (
        <div id="splash">
          <img src="/assets/images/loading.gif" style={{ width: 300 }} alt="Cargando..." />
        </div>
      )}
      {!showSplash && (
        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
        >
          <div className="chat-container">
            <div className={`chat-header ${headerVisible ? "" : "hidden"}`} ref={headerRef}>
              <div className="commerce">
                <div className="chat-info">
                  <button onClick={handleBack} id="back-btn">
                    <svg
                      style={{ marginLeft: 3 }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M12.727 3.687a1 1 0 1 0-1.454-1.374l-8.5 9a1 1 0 0 0 0 1.374l8.5 9.001a1 1 0 1 0 1.454-1.373L4.875 12z"
                      />
                    </svg>
                  </button>
                  <img
                    src={comercio.logo || "/assets/images/default.png"}
                    alt="Comercio"
                    onClick={irADetalleComercio}
                  />
                  <div className="commerce-info">
                    <h3>
                      {comercio.nombre}
                      <img src="/assets/images/verified.svg" className="verifiedUser" style={{ width: 300 }} alt="Verificado" />
                    </h3>
                    <p className="typing-status">
                      {indiceRespuesta !== null
                        ? `Generalmente responde en ${
                            indiceRespuesta < 60
                              ? `${indiceRespuesta} minutos`
                              : `${Math.round(indiceRespuesta / 60)} horas`
                          }.`
                        : "Sin índice de respuesta aún."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="chat-messages" ref={chatRef}>
              {[...mensajes]
                .sort((a, b) => {
                  const fechaA = new Date(a.timestamp);
                  const fechaB = new Date(b.timestamp);
                  return fechaA.getTime() === fechaB.getTime()
                    ? a.id - b.id
                    : fechaA - fechaB;
                })
                .map((msg, index) => (
                  <div
                    key={index}
                    className={`message ${
                      msg.es_comercio === 1
                        ? "received"
                        : msg.id_perfil === user.id
                        ? "sent"
                        : "received"
                    }`}
                  >
                    <p>{msg.texto}</p>
                    <span className="timestamp">{msg.timestamp_formateado}</span>
                  </div>
                ))}
            </div>
            <div className="chat-input-container">
              <input
                type="text"
                className="chat-input"
                placeholder="Escribe un mensaje..."
                value={nuevoMensaje}
                onChange={(e) => setNuevoMensaje(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleEnviar()}
              />
              <button className="send-btn" onClick={handleEnviar}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="#fff"
                    d="M20.33 3.67a1.45 1.45 0 0 0-1.47-.35L4.23 8.2A1.44 1.44 0 0 0 4 10.85l6.07 3l3 6.09a1.44 1.44 0 0 0 1.29.79h.1a1.43 1.43 0 0 0 1.26-1l4.95-14.59a1.41 1.41 0 0 0-.34-1.47M4.85 9.58l12.77-4.26l-7.09 7.09Zm9.58 9.57l-2.84-5.68l7.09-7.09Z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default Chat;
